import type { PermissionCatalogueGroup } from '../api/types';

const CATEGORY_LABELS: Record<string, string> = {
  lead: 'Leads',
  config: 'Configuration',
  admin: 'Administration',
  report: 'Reporting',
};

const CATEGORY_HELP: Record<string, string> = {
  lead: 'Day-to-day work on leads',
  config: 'Changing how the CRM itself is set up',
  admin: 'Managing people and integrations',
  report: 'Dashboards and analytics',
};

export interface PermissionState {
  /** Whether the box is ticked. */
  checked: boolean;
  /** Present only in the per-user view. */
  fromRole?: boolean;
  override?: 'grant' | 'revoke' | null;
}

/**
 * The checkbox grid used for both roles and individual users.
 *
 * For a role it is a plain on/off list. For a user, each row also shows how the
 * value was arrived at — inherited from the role, or an explicit exception —
 * because "why does Priya have this?" is the question an admin actually asks.
 */
export function PermissionMatrix({
  groups,
  state,
  onToggle,
  onReset,
  disabled = false,
  showOrigin = false,
}: {
  groups: PermissionCatalogueGroup[];
  state: Record<string, PermissionState>;
  onToggle: (key: string, next: boolean) => void;
  onReset?: (key: string) => void;
  disabled?: boolean;
  showOrigin?: boolean;
}) {
  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {groups.map((group) => {
        const keys = group.permissions.map((p) => p.key);
        const selected = keys.filter((k) => state[k]?.checked).length;
        const allOn = selected === keys.length;

        return (
          <div key={group.category}>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 10,
                marginBottom: 6,
                paddingBottom: 6,
                borderBottom: '1px solid var(--rule)',
              }}
            >
              <h4 style={{ margin: 0, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)' }}>
                {CATEGORY_LABELS[group.category] ?? group.category}
              </h4>
              <span style={{ fontSize: 12, color: 'var(--faint)' }}>
                {CATEGORY_HELP[group.category]}
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--muted)' }}>
                {selected}/{keys.length}
              </span>
              {!disabled && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => keys.forEach((k) => onToggle(k, !allOn))}
                >
                  {allOn ? 'Clear all' : 'Select all'}
                </button>
              )}
            </div>

            <div style={{ display: 'grid', gap: 2 }}>
              {group.permissions.map((permission) => {
                const s = state[permission.key] ?? { checked: false };
                const isOverride = Boolean(s.override);

                return (
                  <label
                    key={permission.key}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '20px 1fr auto',
                      gap: 10,
                      alignItems: 'start',
                      padding: '7px 8px',
                      borderRadius: 'var(--r-sm)',
                      marginBottom: 0,
                      fontWeight: 400,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      background: isOverride ? 'var(--accent-bg)' : undefined,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={s.checked}
                      disabled={disabled}
                      onChange={(e) => onToggle(permission.key, e.target.checked)}
                      style={{ width: 'auto', marginTop: 2 }}
                    />
                    <span>
                      <span style={{ display: 'block', fontSize: 13.5, color: 'var(--ink)' }}>
                        {permission.description}
                      </span>
                      <code style={{ fontSize: 11.5, background: 'none', border: 0, padding: 0, color: 'var(--muted)' }}>
                        {permission.key}
                      </code>
                    </span>

                    {showOrigin && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                        {s.override === 'grant' && <span className="chip chip-plain" style={{ color: 'var(--good)' }}>added</span>}
                        {s.override === 'revoke' && <span className="chip chip-plain" style={{ color: 'var(--danger)' }}>removed</span>}
                        {!s.override && s.fromRole && (
                          <span className="chip chip-plain" title="Comes from this user's role">from role</span>
                        )}
                        {isOverride && onReset && !disabled && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={(e) => {
                              e.preventDefault();
                              onReset(permission.key);
                            }}
                            title="Go back to whatever the role says"
                          >
                            reset
                          </button>
                        )}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
